import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { PT, AUTH_LOGO_URL } from './portalUi';

// Control styling shared by every field/button/link inside an auth card, so the
// forms stay plain MUI while picking up the dark-teal treatment.
const controlsSx = {
  '& h1,& h2,& h4,& h5,& .MuiTypography-h4,& .MuiTypography-h5,& .MuiTypography-h6': {
    color: PT.text,
    fontWeight: 800
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: PT.text,
    '& fieldset': { borderColor: PT.border },
    '&:hover fieldset': { borderColor: 'rgba(56,232,255,0.5)' },
    '&.Mui-focused fieldset': {
      borderColor: PT.teal,
      boxShadow: '0 0 0 3px rgba(56,232,255,0.18)'
    }
  },
  '& .MuiInputLabel-root': { color: PT.textMuted },
  '& .MuiInputLabel-root.Mui-focused': { color: PT.teal },
  '& .MuiFormHelperText-root': { color: PT.textMuted },
  '& .MuiButton-contained': {
    background: `linear-gradient(180deg, ${PT.teal}, ${PT.tealDeep})`,
    color: '#04222a',
    fontWeight: 800,
    borderRadius: '12px',
    padding: '10px 16px',
    boxShadow: '0 10px 26px rgba(56,232,255,0.32)',
    '&:hover': { background: `linear-gradient(180deg, #63eeff, ${PT.teal})` },
    '&.Mui-disabled': { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }
  },
  '& a, & .MuiLink-root': {
    color: PT.teal,
    textDecorationColor: 'rgba(56,232,255,0.5)'
  },
  '& .MuiCheckbox-root': { color: PT.textMuted, '&.Mui-checked': { color: PT.teal } },
  '& .MuiSelect-icon': { color: PT.textMuted }
};

// Full-screen auth background: the portal's near-black with a soft teal/gold
// aurora — a modern, video-free replacement for the old promo split.
export function AuthShell({
  children,
  maxWidth = 460
}: {
  children: ReactNode;
  maxWidth?: number;
}) {
  return (
    <Box
      className="auth-shell"
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: { xs: 4, sm: 6 },
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: PT.page,
        backgroundImage:
          'radial-gradient(55% 45% at 12% 8%, rgba(56,232,255,0.18), transparent 60%),' +
          'radial-gradient(45% 45% at 88% 16%, rgba(56,232,255,0.10), transparent 55%),' +
          'radial-gradient(60% 55% at 82% 98%, rgba(243,191,77,0.06), transparent 55%)'
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth }}>{children}</Box>
    </Box>
  );
}

// A frosted glass card that holds the form, topped with the INCRYPT logo.
export function AuthCard({
  children,
  logo = true
}: {
  children: ReactNode;
  logo?: boolean;
}) {
  return (
    <Box
      sx={{
        borderRadius: '22px',
        border: '1px solid rgba(56,232,255,0.28)',
        background: 'linear-gradient(180deg, rgba(20,34,50,0.80), rgba(11,20,32,0.76))',
        backdropFilter: 'blur(22px) saturate(140%)',
        WebkitBackdropFilter: 'blur(22px) saturate(140%)',
        boxShadow: '0 30px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)',
        color: PT.text,
        p: { xs: 3, sm: 4 },
        ...controlsSx
      }}
    >
      {logo && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Box component="img" src={AUTH_LOGO_URL} alt="INCRYPT" sx={{ height: 40 }} />
        </Box>
      )}
      {children}
    </Box>
  );
}
